"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    X, History, Upload, Link2, Search, Loader2, Check,
    ImageIcon, FolderOpen, AlertCircle, Plus
} from "lucide-react";

interface UploadedFile {
    id: number;
    key: string;
    url: string;
    signedUrl: string;
    source: string;
    originalName: string | null;
    sizeBytes: number;
    createdAt: string;
}

interface FilePickerModalProps {
    onSelect: (url: string) => void;
    onClose: () => void;
    accept?: string;
}

type Tab = "history" | "upload" | "url";

export const FilePickerModal: React.FC<FilePickerModalProps> = ({ onSelect, onClose, accept }) => {
    const [tab, setTab] = useState<Tab>("history");
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [urlInput, setUrlInput] = useState("");
    const [isFetchingUrl, setIsFetchingUrl] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadFiles = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch("/api/files");
            const data = await res.json();
            setFiles(data.uploadedFiles ?? []);
        } catch { } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadFiles(); }, [loadFiles]);

    // Esc to close
    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        document.body.style.overflow = "hidden";
        return () => { document.removeEventListener("keydown", h); document.body.style.overflow = ""; };
    }, [onClose]);

    const handleSelect = (url: string) => {
        setSelected(url);
    };

    const handleConfirm = () => {
        if (selected) onSelect(selected);
    };

    const handleFileUpload = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        setIsUploading(true);
        setUploadError(null);
        try {
            for (const file of Array.from(fileList)) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await fetch("/api/upload/product", { method: "POST", body: fd });
                if (!res.ok) throw new Error("上传失败");
                const data = await res.json();
                // Immediately select and close
                onSelect(data.url);
                return;
            }
        } catch (e: any) {
            setUploadError(e.message);
        } finally {
            setIsUploading(false);
            loadFiles();
        }
    };

    const handleUrlFetch = async () => {
        if (!urlInput.trim()) return;
        setIsFetchingUrl(true);
        setUploadError(null);
        try {
            const res = await fetch("/api/upload/from-url", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: urlInput.trim() }),
            });
            if (!res.ok) {
                const e = await res.json();
                throw new Error(e.error || "获取失败");
            }
            const data = await res.json();
            onSelect(data.url);
        } catch (e: any) {
            setUploadError(e.message);
        } finally {
            setIsFetchingUrl(false);
        }
    };

    const filteredFiles = files.filter(f =>
        !search || (f.originalName || f.key).toLowerCase().includes(search.toLowerCase())
    );

    const tabBtn = (t: Tab, icon: React.ReactNode, label: string) => (
        <button
            onClick={() => setTab(t)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 ${tab === t
                ? "border-purple-500 text-purple-600 dark:text-purple-400"
                : "border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"}`}
        >
            {icon}{label}
        </button>
    );

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden border border-zinc-200 dark:border-zinc-700">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/10 rounded-lg">
                            <FolderOpen size={16} className="text-purple-500" />
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">选择文件</h2>
                            <p className="text-[10px] text-zinc-400">从历史记录选择，或上传新文件</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-zinc-200 dark:border-zinc-800 px-4 bg-zinc-50 dark:bg-zinc-900/50">
                    {tabBtn("history", <History size={14} />, `历史文件 (${files.length})`)}
                    {tabBtn("upload", <Upload size={14} />, "上传文件")}
                    {tabBtn("url", <Link2 size={14} />, "URL 获取")}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* History Tab */}
                    {tab === "history" && (
                        <div className="flex flex-col h-full">
                            <div className="px-4 pt-4 pb-2">
                                <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="搜索文件名..."
                                        className="w-full pl-9 pr-4 py-2 text-sm bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg outline-none focus:border-purple-400 transition-colors text-zinc-800 dark:text-zinc-200"
                                    />
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto px-4 pb-4">
                                {isLoading ? (
                                    <div className="flex items-center justify-center h-40 text-zinc-400">
                                        <Loader2 size={24} className="animate-spin mr-2" /> 加载中...
                                    </div>
                                ) : filteredFiles.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-40 gap-3 text-zinc-400">
                                        <ImageIcon size={36} strokeWidth={1} />
                                        <p className="text-sm">{search ? "未找到匹配文件" : "暂无历史上传记录"}</p>
                                        <button onClick={() => setTab("upload")} className="text-xs text-purple-500 hover:underline">
                                            上传第一个文件
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 pt-2">
                                        {filteredFiles.map((f) => (
                                            <button
                                                key={f.id}
                                                onClick={() => handleSelect(f.signedUrl)}
                                                onDoubleClick={() => { handleSelect(f.signedUrl); onSelect(f.signedUrl); }}
                                                className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all focus:outline-none ${selected === f.signedUrl
                                                    ? "border-purple-500 ring-2 ring-purple-400/40"
                                                    : "border-zinc-200 dark:border-zinc-700 hover:border-purple-300 dark:hover:border-purple-600"}`}
                                            >
                                                <img
                                                    src={f.signedUrl}
                                                    alt={f.originalName || f.key}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { (e.target as HTMLImageElement).style.opacity = "0.2"; }}
                                                />
                                                {selected === f.signedUrl && (
                                                    <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                                                        <div className="bg-purple-500 rounded-full p-1">
                                                            <Check size={12} className="text-white" />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <p className="text-[9px] text-white truncate">{f.originalName || f.key.split("/").pop()}</p>
                                                    <p className="text-[8px] text-white/60">{f.sizeBytes > 0 ? `${(f.sizeBytes / 1024).toFixed(0)}KB` : ""}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Upload Tab */}
                    {tab === "upload" && (
                        <div className="flex flex-col items-center justify-center h-full px-8 gap-6">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={accept || "image/jpeg,image/png,image/webp"}
                                className="sr-only"
                                onChange={(e) => handleFileUpload(e.target.files)}
                            />
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragging(false);
                                    handleFileUpload(e.dataTransfer.files);
                                }}
                                className={`w-full max-w-md border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${isDragging
                                    ? "border-purple-400 bg-purple-50 dark:bg-purple-500/10"
                                    : "border-zinc-300 dark:border-zinc-600 hover:border-purple-400 dark:hover:border-purple-500 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"}`}
                            >
                                {isUploading ? (
                                    <div className="flex flex-col items-center gap-3 text-purple-500">
                                        <Loader2 size={36} className="animate-spin" />
                                        <p className="text-sm font-medium">上传中...</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-3 text-zinc-400">
                                        <div className="p-4 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                                            <Upload size={32} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300">点击选择或拖拽文件</p>
                                            <p className="text-xs mt-1">支持 JPG、PNG、WebP</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {uploadError && (
                                <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2 rounded-lg">
                                    <AlertCircle size={12} /> {uploadError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* URL Tab */}
                    {tab === "url" && (
                        <div className="flex flex-col items-center justify-center h-full px-8 gap-4">
                            <div className="w-full max-w-lg">
                                <div className="p-4 bg-zinc-100 dark:bg-zinc-800 rounded-2xl mb-6 flex items-center justify-center">
                                    <Link2 size={36} className="text-zinc-400" />
                                </div>
                                <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-2">图片 URL</label>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={urlInput}
                                        onChange={(e) => setUrlInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleUrlFetch(); }}
                                        placeholder="https://example.com/image.jpg"
                                        className="flex-1 px-4 py-2.5 text-sm bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl outline-none focus:border-purple-400 transition-colors text-zinc-800 dark:text-zinc-200"
                                    />
                                    <button
                                        onClick={handleUrlFetch}
                                        disabled={!urlInput.trim() || isFetchingUrl}
                                        className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-200 dark:disabled:bg-zinc-700 disabled:text-zinc-400 text-white text-sm font-medium rounded-xl transition-all flex items-center gap-2"
                                    >
                                        {isFetchingUrl ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                        {isFetchingUrl ? "获取中..." : "获取"}
                                    </button>
                                </div>
                                <p className="text-xs text-zinc-400 mt-2">服务端将拉取该图片并保存到对象存储</p>
                            </div>
                            {uploadError && (
                                <div className="flex items-center gap-2 text-xs text-red-500 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2 rounded-lg w-full max-w-lg">
                                    <AlertCircle size={12} /> {uploadError}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {tab === "history" && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
                        <p className="text-xs text-zinc-400">
                            {selected ? "已选择 1 个文件 · 双击可快速确认" : "点击选择文件"}
                        </p>
                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors">
                                取消
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={!selected}
                                className="px-5 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-200 dark:disabled:bg-zinc-700 disabled:text-zinc-400 text-white text-sm font-medium rounded-xl transition-all"
                            >
                                确认选择
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
